/**
 * Google Tag Manager (GTM) Analytics Wrapper
 *
 * This module provides a simple interface for pushing events to GTM's dataLayer.
 * It replaces the previous Segment integration while maintaining type safety.
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, any>>;
  }
}

/**
 * GTM DataLayer Event interface
 */
export interface DataLayerEvent {
  event: string;
  [key: string]: any;
}

export interface UserTraits {
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  [key: string]: any;
}

export interface PageProperties {
  url?: string;
  path?: string;
  referrer?: string;
  title?: string;
  brand?: string;
  [key: string]: any;
}

/**
 * Pushes an event to the GTM dataLayer
 *
 * @param event - The event name (e.g., 'Product Viewed', 'Checkout Started')
 * @param data - Additional data to include with the event
 */
export function pushEvent(event: string, data: Record<string, any> = {}): void {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot push event - window is undefined (server-side)');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...data,
  });
}

/**
 * Identifies a user by sending user traits to GTM
 *
 * @param userId - The unique user identifier (typically email or customer ID)
 * @param traits - Additional user properties
 */
export function identifyUser(userId: string, traits: UserTraits = {}): void {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot identify user - window is undefined (server-side)');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_identified',
    userId,
    user: {
      ...traits,
    },
  });
}

/**
 * Tracks a page view event
 *
 * @param pageName - Name or title of the page
 * @param properties - Additional page properties
 */
export function trackPage(pageName: string, properties: PageProperties = {}): void {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot track page - window is undefined (server-side)');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page: pageName,
    ...properties,
  });
}

/**
 * Resets the user identification (typically on logout)
 */
export function resetUser(): void {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot reset user - window is undefined (server-side)');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_reset',
    userId: undefined,
    user: undefined,
  });
}
